import React from 'react';
import { FaGlobe, FaBuilding, FaStar, FaClock, FaUmbrellaBeach, FaUsers, FaHeart } from 'react-icons/fa';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPriceSync } from '../../utils/currency';

const CountryCitySelection = ({ 
  countries, 
  cities, 
  activities,
  selectedCountry, 
  selectedCity,
  selectedActivity,
  handleCountrySelect, 
  handleCitySelect,
  handleActivitySelect,
  API_BASE_URL 
}) => {
  const { currency } = useCurrency();
  return (
    <div className="space-y-8">
      {/* Countries Horizontal Scroller */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Select Country</h3>
          <p className="text-sm text-gray-600">Choose your destination country</p>
        </div>
        <div className="overflow-x-auto pb-4 -mx-2 px-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <style>{`
            .scrollbar-hide::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          <div className="flex space-x-4 min-w-max">
            {countries.map((country) => {
              const imageUrl = country.image
                ? `${API_BASE_URL.replace("/api/activity", "")}/${country.image.replace(/\\/g, "/")}`
                : null;
              const isSelected = selectedCountry?._id === country._id;

              return (
                <div
                  key={country._id}
                  onClick={() => handleCountrySelect(country)}
                  className={`
                    flex-shrink-0 w-64 cursor-pointer transition-all duration-300 transform hover:scale-105
                    ${isSelected ? 'ring-4 ring-blue-500 ring-offset-2' : ''}
                  `}
                >
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-transparent hover:border-blue-300">
                    {imageUrl ? (
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={country.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        <div className="absolute top-3 left-3">
                          <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                            <span className="text-sm font-bold text-white">
                              {country.code}
                            </span>
                          </div>
                        </div>
                        <div className="absolute bottom-3 left-3 right-3">
                          <h3 className="text-lg font-bold text-white mb-1">
                            {country.name}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-200">
                              {country.currency} • {country.currencySymbol}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="h-40 bg-gradient-to-br from-blue-500 to-purple-600 flex flex-col items-center justify-center p-4">
                        <FaGlobe className="text-4xl text-white/90 mb-2" />
                        <h3 className="text-lg font-bold text-white text-center">
                          {country.name}
                        </h3>
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          country.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {country.active ? 'Available' : 'Coming Soon'}
                        </span>
                        <div className="flex items-center text-yellow-500">
                          <FaStar className="w-3 h-3" />
                          <span className="ml-1 text-xs font-medium">4.5</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cities Horizontal Scroller - Only show if country is selected */}
      {selectedCountry && (
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Cities in {selectedCountry.name}
            </h3>
            <p className="text-sm text-gray-600">
              {cities.length > 0 ? `Choose from ${cities.length} amazing cities` : 'Loading cities...'}
            </p>
          </div>
          {cities.length > 0 ? (
            <div className="overflow-x-auto pb-4 -mx-2 px-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="flex space-x-4 min-w-max">
                {cities.map((city) => {
                  const imageUrl = city.image
                    ? `${API_BASE_URL.replace("/api/activity", "")}/${city.image.replace(/\\/g, "/")}`
                    : null;
                  const isSelected = selectedCity?._id === city._id;

                  return (
                    <div
                      key={city._id}
                      onClick={() => handleCitySelect(city)}
                      className={`
                        flex-shrink-0 w-64 cursor-pointer transition-all duration-300 transform hover:scale-105
                        ${isSelected ? 'ring-4 ring-purple-500 ring-offset-2' : ''}
                      `}
                    >
                      <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-transparent hover:border-purple-300">
                        {imageUrl ? (
                          <div className="relative h-40 overflow-hidden">
                            <img
                              src={imageUrl}
                              alt={city.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                            <div className="absolute bottom-3 left-3 right-3">
                              <h3 className="text-lg font-bold text-white mb-2">
                                {city.name}
                              </h3>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <FaClock className="text-gray-200 w-3 h-3" />
                                  <span className="text-xs text-gray-200">
                                    {city.timezone}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <FaStar className="text-yellow-400 w-3 h-3" />
                                  <span className="text-xs font-medium text-white">4.7</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="h-40 bg-gradient-to-br from-purple-500 to-pink-600 flex flex-col items-center justify-center p-4">
                            <FaBuilding className="text-4xl text-white/90 mb-2" />
                            <h3 className="text-lg font-bold text-white text-center">
                              {city.name}
                            </h3>
                            <p className="text-white/80 text-xs mt-1">{city.timezone}</p>
                          </div>
                        )}
                        <div className="p-3">
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                              city.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {city.active ? 'Popular' : 'Limited'}
                            </span>
                            <span className="text-xs text-gray-500">
                              50+ activities
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
              <FaBuilding className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No cities available for this country</p>
            </div>
          )}
        </div>
      )}

      {/* Activities Horizontal Scroller - Only show if city is selected */}
      {selectedCity && (
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Activities in {selectedCity.name}
            </h3>
            <p className="text-sm text-gray-600">
              {activities.length > 0 ? `Discover ${activities.length} amazing experiences` : 'Loading activities...'}
            </p>
          </div>
          {activities.length > 0 ? (
            <div className="overflow-x-auto pb-4 -mx-2 px-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="flex space-x-4 min-w-max">
                {activities.map((activity) => {
                  const imageUrl = activity.images && activity.images.length > 0
                    ? `${API_BASE_URL.replace("/api/activity", "")}/${activity.images[0].replace(/\\/g, "/")}`
                    : null;
                  const isSelected = selectedActivity?._id === activity._id;

                  return (
                    <div
                      key={activity._id}
                      onClick={() => handleActivitySelect(activity)}
                      className={`
                        flex-shrink-0 w-80 cursor-pointer transition-all duration-300 transform hover:scale-105
                        ${isSelected ? 'ring-4 ring-green-500 ring-offset-2' : ''}
                      `}
                    >
                      <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-transparent hover:border-green-300">
                        {imageUrl ? (
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={imageUrl}
                              alt={activity.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                            <div className="absolute top-3 right-3">
                              <button 
                                className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <FaHeart className="text-gray-600 hover:text-red-500 text-sm" />
                              </button>
                            </div>
                            {activity.images && activity.images.length > 1 && (
                              <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">
                                <span className="text-xs font-medium text-white">
                                  +{activity.images.length - 1} photos
                                </span>
                              </div>
                            )}
                            <div className="absolute bottom-3 left-3 right-3">
                              <h3 className="text-base font-bold text-white mb-1.5 line-clamp-1">
                                {activity.name}
                              </h3>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center space-x-1">
                                  <FaStar className="text-yellow-400 w-3 h-3" />
                                  <span className="text-xs font-medium text-white">4.8</span>
                                  <span className="text-xs text-gray-300">(124)</span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3 text-xs text-gray-200">
                                <div className="flex items-center">
                                  <FaClock className="mr-1 w-2.5 h-2.5" />
                                  <span>{activity.duration} mins</span>
                                </div>
                                <div className="flex items-center">
                                  <FaUsers className="mr-1 w-2.5 h-2.5" />
                                  <span>Max {activity.maxCapacity}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="h-48 bg-gradient-to-br from-teal-500 to-green-600 flex flex-col items-center justify-center p-4">
                            <FaUmbrellaBeach className="text-4xl text-white/90 mb-2" />
                            <h3 className="text-lg font-bold text-white text-center">
                              {activity.name}
                            </h3>
                          </div>
                        )}
                        <div className="p-4">
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {activity.description || 'Experience the best of ' + selectedCity.name}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                              activity.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {activity.status === 'active' ? 'Available Now' : 'Limited Slots'}
                            </span>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">Starting from</div>
                              <div className="text-sm font-bold text-gray-900">
                                {formatPriceSync(Math.floor(Math.random() * 10000) + 50, currency)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
              <FaUmbrellaBeach className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No activities available for this city</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CountryCitySelection;
